import { useCallback, useEffect, useState } from 'react';
import { CareGrid } from '@components/grid/CareGrid';
import { useGridView, type GridColumn } from '@components/grid/useGridView';
import type { SlicerDef } from '@lib/grid';
import {
  uspsRejectionDecide, uspsRejections,
  type PostalAddress, type RejectionState, type UspsRejectionRow, type UspsRejectionsResp,
} from '@lib/care-api';
import { fmtMemberId } from '@lib/member-id';
import { useStaleRead } from '@lib/stale-read';
import { LOCK_FIELDSET_STYLE, StaleNote } from '@components/StaleNote';
import { fmtTs, emptyNotice, emptyRow, emptyTone, type EmptyReason } from '@lib/care-model';

/**
 * USPS rejections (nav 11), William's row 218 of 15 September 2026: "a table of all of the USPS
 * rejections ... what we have and what USPS says. If marny clears these (in a table edit form) then
 * push these back into the approved list. ... if Marny says they cannot be handled ... a report for
 * the client of that."
 *
 * One row per piece USPS could not deliver: our address (editable), USPS's reason and where and
 * when it said so, and the decision. CLEARED carries the corrected or confirmed address and puts
 * the member back on the mailable file at the Portal's next build; CANNOT BE HANDLED carries a
 * reason and is the client report. Neither writes a database: each is one row in the address
 * decisions record, and a change of mind is a later row.
 *
 * WHAT USPS DOES NOT TELL US, said on the screen rather than implied by a column: the tracking
 * scan gives a reason and a place, never a corrected address. The "USPS says" column is exactly
 * that. The roster's own USPS check (some campaigns) does carry a standardized address, shown when
 * it exists.
 */

const KIND_LABEL: Record<UspsRejectionRow['kind'], string> = {
  kit: 'Kit',
  roster: 'Roster check',
  letter: 'Letter',
};

const DECISION_WORDING = {
  CLEARED: { label: 'Cleared', pill: 'pill-success', help: 'The address was corrected or confirmed. The member goes back on the mailing list at the next update.' },
  CANNOT_BE_HANDLED: { label: 'Cannot be handled', pill: 'pill-danger', help: 'Nothing more can be done from here. The member stays off the mailable file and the reason goes on the client report.' },
} as const;

function fmtAddress(a: PostalAddress | null): string {
  if (!a) return '';
  const zip = a.zip4 ? `${a.zip}-${a.zip4}` : a.zip;
  return [a.line1, a.line2, [a.city, a.state].filter(Boolean).join(', '), zip].filter(Boolean).join(' / ');
}

/**
 * ROW 356: the slicers, over the whole list (this list arrives whole, one campaign at a time): the
 * piece, the Post Office's code, and what the Post Office said. The state keeps its own chips.
 */
const USPS_SLICERS: SlicerDef<UspsRejectionRow>[] = [
  { key: 'piece', label: 'Piece', value: (r) => r.kind, labelFor: (v) => KIND_LABEL[v as UspsRejectionRow['kind']] ?? v },
  { key: 'code', label: 'USPS code', value: (r) => r.uspsCode || r.carrierDetail, blankLabel: 'Not recorded yet' },
  { key: 'reason', label: 'What USPS says', value: (r) => r.uspsReason, blankLabel: 'Nothing said', searchPast: 8 },
];

/**
 * Row 265: the notation Frontend's build writes on the member's file from a CLEARED decision, in
 * William's words ("CC clears on date, time for delivery"). Shown in the confirm so she knows what
 * her click puts on the record. US long form, Pacific, like the rest of the console.
 */
export function clearNotation(at: Date): string {
  const d = at.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', year: 'numeric' });
  const t = at.toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: 'numeric', minute: '2-digit' });
  return `CC clears on ${d}, ${t} for delivery`;
}

const EMPTY_ADDRESS: PostalAddress = { line1: '', line2: '', city: '', state: '', zip: '', zip4: '' };

export function UspsRejections(): JSX.Element {
  const [state, setState] = useState<RejectionState>('open');
  /** Row 265: '' is the client's default campaign; a slug is one the API listed. */
  const [campaign, setCampaign] = useState('');
  const [data, setData] = useState<UspsRejectionsResp | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  /** The row whose address form is open, and its draft. One at a time; the draft starts from our address. */
  const [editing, setEditing] = useState<string>('');
  const [draft, setDraft] = useState<PostalAddress>(EMPTY_ADDRESS);
  const [notes, setNotes] = useState<Record<string, string>>({});

  // Row 359 B: a failed RE-read of the view on screen keeps its rows, stale and locked (lib/stale-read).
  const viewKey = JSON.stringify([state, campaign]);
  const { staleAt, ok: readOk, failed: readFailed } = useStaleRead();

  const load = useCallback(async () => {
    setErr('');
    try {
      setData(await uspsRejections(state, campaign));
      readOk(viewKey);
    } catch (e) {
      // A failed read of a NEW view shows an error and NO rows, never a stale list on a screen that
      // re-enters members into a mailing; a failed RE-read keeps them, locked, under a red line.
      if (!readFailed(viewKey)) setData(null);
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [state, campaign, viewKey, readOk, readFailed]);

  useEffect(() => { void load(); }, [load]);


  function openForm(row: UspsRejectionRow): void {
    setEditing(row.rowId);
    // Start from the corrected address when one was already recorded, else from ours.
    setDraft(row.decision?.address ?? row.ours);
  }

  async function decide(row: UspsRejectionRow, decision: 'CLEARED' | 'CANNOT_BE_HANDLED'): Promise<void> {
    const note = (notes[row.rowId] ?? '').trim();
    if (decision === 'CANNOT_BE_HANDLED' && !note) {
      setErr('A reason is required when a rejection cannot be handled: it is what the client reads on the report.');
      return;
    }
    const address = decision === 'CLEARED' ? (editing === row.rowId ? draft : (row.decision?.address ?? row.ours)) : null;
    if (decision === 'CLEARED' && !window.confirm(
      `Clear this rejection for ${row.memberName || fmtMemberId(row.memberId)} with the address\n${fmtAddress(address)}\n\nThe member's file will read "${clearNotation(new Date())}" and the member goes back on the mailing list at the next update.`,
    )) return;
    setBusy(row.rowId);
    setErr('');
    try {
      const r = await uspsRejectionDecide(row.rowId, decision, address, note, campaign || row.campaign);
      if (!r.ok) {
        setErr(String((r.data as { error?: unknown }).error ?? `Request failed (${r.status})`));
        return;
      }
      setNotes((n) => ({ ...n, [row.rowId]: '' }));
      if (editing === row.rowId) setEditing('');
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('');
    }
  }

  /**
   * ROW 356: the list's columns for the shared table. Each cell is the cell this list has always
   * shown; the sort runs over the whole list in the browser (a code not recorded sorts last).
   */
  function uspsColumns(): GridColumn<UspsRejectionRow>[] {
    return [
      {
        key: 'member', label: 'Member', sortValue: (r) => r.memberName,
        render: (r) => (
          <>
            <div>{r.memberName || <span className="muted">unmatched</span>}</div>
            <div className="micro muted">{r.memberId ? fmtMemberId(r.memberId) : ''}</div>
          </>
        ),
      },
      { key: 'campaign', label: 'Campaign', tdClassName: 'micro', sortValue: (r) => r.campaign, render: (r) => r.campaign },
      {
        key: 'kind', label: 'Piece', sortValue: (r) => KIND_LABEL[r.kind],
        render: (r) => (
          <>
            <div>{KIND_LABEL[r.kind]}</div>
            {/* Row 355: a kit keeps its barcode (she reads it to the member); a letter's reference was a
                database table name and a roster row's was the member id already shown. */}
            <div className="micro muted">{r.kind === 'kit' ? r.ref : ''}{r.sentAt ? `${r.kind === 'kit' ? ' - ' : ''}sent ${r.sentAt}` : ''}</div>
          </>
        ),
      },
      { key: 'have', label: 'What we have', tdStyle: { minWidth: 260 }, render: (r) => haveCell(r) },
      {
        key: 'code', label: 'USPS code', tdClassName: 'micro', sortValue: (r) => r.uspsCode || r.carrierDetail,
        render: (r) =>
          r.uspsCode || (r.carrierDetail ? (
            <span title="EasyPost's reason on the failing scan. USPS bulk tracking sends no carrier code for these letters, so this is EasyPost's wording, not a USPS event code.">
              {r.carrierDetail} <span className="muted">(EasyPost)</span>
            </span>
          ) : <span className="muted" title="The Post Office's own code is not recorded for this kind of mail yet.">not recorded yet</span>),
      },
      {
        key: 'reason', label: 'What USPS says', tdStyle: { maxWidth: 260 }, sortValue: (r) => r.uspsReason,
        render: (r) => (
          <>
            <div>{r.uspsReason || <span className="muted">-</span>}</div>
            {r.uspsPlace && <div className="micro muted">{r.uspsPlace}</div>}
          </>
        ),
      },
      {
        key: 'when', label: 'When', firstDir: 'desc', sortValue: (r) => Date.parse(r.uspsAt || r.sentAt) || null,
        render: (r) => (r.uspsAt ? fmtTs(r.uspsAt) : <span className="muted">-</span>),
      },
      { key: 'decision', label: 'Decision', locked: true, tdStyle: { minWidth: 320 }, render: (r) => decisionCell(r) },
    ];
  }

  /** What we have: the address, or the form while this row is being edited. */
  function haveCell(r: UspsRejectionRow): JSX.Element {
    const isEditing = editing === r.rowId;
    const d = r.decision;
    return (
      <>
        {isEditing ? (
          <div style={{ display: 'grid', gap: 4 }}>
            <input className="text-input" aria-label="Address line 1" placeholder="Address line 1" value={draft.line1} onChange={(e) => setDraft({ ...draft, line1: e.target.value })} />
            <input className="text-input" aria-label="Address line 2" placeholder="Apt, suite, unit" value={draft.line2} onChange={(e) => setDraft({ ...draft, line2: e.target.value })} />
            <div style={{ display: 'flex', gap: 4 }}>
              <input className="text-input" aria-label="City" placeholder="City" value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
              <input className="text-input" aria-label="State" placeholder="ST" style={{ width: 56 }} maxLength={2} value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value.toUpperCase() })} />
              <input className="text-input" aria-label="ZIP" placeholder="ZIP" style={{ width: 80 }} maxLength={5} value={draft.zip} onChange={(e) => setDraft({ ...draft, zip: e.target.value.replace(/\D/g, '') })} />
              <input className="text-input" aria-label="ZIP+4" placeholder="+4" style={{ width: 64 }} maxLength={4} value={draft.zip4} onChange={(e) => setDraft({ ...draft, zip4: e.target.value.replace(/\D/g, '') })} />
            </div>
            <div className="micro muted">
              {d?.address ? `recorded: ${fmtAddress(d.address)}` : `on file: ${fmtAddress(r.ours)}`}
            </div>
          </div>
        ) : (
          <div>
            <div>{fmtAddress(d?.address ?? r.ours) || <span className="muted">no address on file</span>}</div>
            {d?.address && <div className="micro muted">was: {fmtAddress(r.ours)}</div>}
            {r.usps && <div className="micro muted">USPS standardized: {fmtAddress(r.usps)}</div>}
          </div>
        )}
      </>
    );
  }

  /** The decision already recorded, then the reason box and the three buttons. */
  function decisionCell(r: UspsRejectionRow): JSX.Element {
    const isEditing = editing === r.rowId;
    const d = r.decision;
    return (
      <>
        {d && (
          <div className="micro" style={{ marginBottom: 6 }}>
            <span className={`pill ${DECISION_WORDING[d.decision].pill}`} title={DECISION_WORDING[d.decision].help}>
              {DECISION_WORDING[d.decision].label}
            </span>{' '}
            by {d.decided_by_name}
            <div className="muted">{fmtTs(d.decided_at)}{d.note ? `: ${d.note}` : ''}</div>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input
            className="text-input"
            aria-label={`Reason for ${r.memberName || r.rowId}`}
            placeholder="Reason (required for cannot be handled; optional when clearing)"
            value={notes[r.rowId] ?? ''}
            onChange={(e) => setNotes((s) => ({ ...s, [r.rowId]: e.target.value }))}
          />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {!isEditing ? (
              <button type="button" className="btn btn-sm btn-ghost" disabled={busy === r.rowId} onClick={() => openForm(r)}>
                Edit address
              </button>
            ) : (
              <button type="button" className="btn btn-sm btn-ghost" disabled={busy === r.rowId} onClick={() => setEditing('')}>
                Cancel edit
              </button>
            )}
            <button type="button" className="btn btn-sm" disabled={busy === r.rowId} onClick={() => void decide(r, 'CLEARED')} title={DECISION_WORDING.CLEARED.help}>
              {isEditing ? 'Save and clear' : 'Clear as is'}
            </button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={busy === r.rowId} onClick={() => void decide(r, 'CANNOT_BE_HANDLED')} title={DECISION_WORDING.CANNOT_BE_HANDLED.help}>
              Cannot be handled
            </button>
          </div>
        </div>
      </>
    );
  }

  const rows = data?.rows ?? [];
  const view = useGridView('usps-rejections', uspsColumns(), { defaultSort: [{ k: 'member', asc: true }], slicers: USPS_SLICERS });
  const counts = data?.counts;
  const reason = data?.emptyReason as EmptyReason | undefined;

  return (
    <div className="page-pad">
      <div className="section-eyebrow">Member Care &middot; USPS rejections</div>
      <h1 className="section-title">USPS rejections</h1>
      <div className="section-rule" />
      <p className="section-lede">
        Every piece of mail USPS could not deliver: the address we hold beside what USPS said, and your decision on it.
      </p>

      <div className="note" style={{ marginBottom: 14 }}>
        USPS reports a reason and where it gave up; it never sends back a corrected address. Fix the address
        here and <strong>Clear</strong> it, and the member goes back on the mailing list at the next update.
        {' '}<strong>Cannot be handled</strong> keeps the member off the file and puts your reason on the
        client report. Neither button changes a database directly; each is a dated row in the address record,
        and a change of mind is a later row.
      </div>

      {reason && reason !== 'no-rows' && (
        <div className={`note ${emptyTone(reason)}`} style={{ marginBottom: 14 }}>
          {emptyNotice(reason, '')}
        </div>
      )}
      {data?.record.error && (
        <div className="note danger" style={{ marginBottom: 14 }}>
          The address decisions record could not be read, so decisions already made may not show: {data.record.error}
        </div>
      )}
      {staleAt ? <StaleNote at={staleAt} err={err} onRetry={() => void load()} />
        : err && <div className="note danger" style={{ marginBottom: 14 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        {([
          ['open', 'Open', counts?.open],
          ['cleared', 'Cleared', counts?.cleared],
          ['cannot-be-handled', 'Cannot be handled', counts?.cannotBeHandled],
          ['all', 'All', data?.total],
        ] as Array<[RejectionState, string, number | undefined]>).map(([key, label, n]) => (
          <button
            key={key}
            type="button"
            className={state === key ? 'chip selected' : 'chip'}
            onClick={() => setState(key)}
          >
            {label}{n ? ` (${n})` : ''}
          </button>
        ))}
        {(data?.campaigns?.length ?? 0) > 1 && (
          <label className="micro muted" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            Campaign
            <select
              className="text-input"
              aria-label="Campaign"
              value={campaign || data?.campaign || ''}
              onChange={(e) => setCampaign(e.target.value)}
            >
              {(data?.campaigns ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        )}
        <span className="micro muted" style={{ marginLeft: 'auto' }}>
          {data ? `campaign ${data.campaign}` : ''}

        </span>
      </div>

      <fieldset disabled={!!staleAt} style={LOCK_FIELDSET_STYLE}>
      <div style={{ overflowX: 'auto' }}>
        <CareGrid
          view={view}
          rows={rows}
          rowKey={(r) => r.rowId}
          noun="pieces"
          empty={err ? '' : emptyRow(reason, state === 'open' ? 'Nothing open. Every rejection has a decision.' : 'Nothing here.')}
        />
      </div>
      </fieldset>
      {/* Row 355: the count of decision files read was system detail; an unreadable one still says so. */}
      {data?.record.filesSkipped ? (
        <div className="micro muted" style={{ marginTop: 10 }}>{`${data.record.filesSkipped} saved decision${data.record.filesSkipped === 1 ? '' : 's'} could not be read.`}</div>
      ) : null}
    </div>
  );
}
